/**
 * Promo pricing — shared by the server checkout and the client booking UI so
 * both compute the same effective price for a class session.
 *
 * A class can carry a temporary promo (promoEnabled + promoPrice). The promo
 * may be windowed by date: when promoStartsAt/promoEndsAt are set, the promo
 * applies only to sessions whose start falls inside the window. With no window
 * (both null) the promo applies to every session, preserving the original
 * always-on behavior.
 *
 * The window is judged by the SESSION date, not the purchase date — June
 * sessions can be free while July sessions of the same class are full price.
 */

export interface PromoPricingClass {
  pricingMode?: string | null;
  price: string | number;
  promoEnabled?: boolean | null;
  promoPrice?: string | number | null;
  promoStartsAt?: string | Date | null;
  promoEndsAt?: string | Date | null;
}

function toTime(value: string | Date | null | undefined): number | null {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isFinite(ms) ? ms : null;
}

/**
 * Whether the class promo applies to a session starting at `sessionStart`.
 * `sessionStart` omitted = no session in context (e.g. a class page with no
 * upcoming sessions): a windowed promo then counts only while its window
 * hasn't ended, so a promo that ran last June doesn't still show as the price.
 */
export function isPromoActiveForSession(
  classDetails: PromoPricingClass,
  sessionStart?: string | Date | null,
  now: Date = new Date()
): boolean {
  const hasPromo =
    classDetails.promoEnabled === true &&
    classDetails.promoPrice !== null &&
    classDetails.promoPrice !== undefined;
  if (!hasPromo) return false;

  const startMs = toTime(classDetails.promoStartsAt);
  const endMs = toTime(classDetails.promoEndsAt);
  if (startMs == null && endMs == null) return true; // unwindowed promo

  const sessionMs = toTime(sessionStart ?? null);
  if (sessionMs == null) return endMs == null || now.getTime() <= endMs;
  if (startMs != null && sessionMs < startMs) return false;
  if (endMs != null && sessionMs > endMs) return false;
  return true;
}

/**
 * Effective per-ticket price (in the same units as `price`) for a session.
 * Free classes are always 0; an applicable promo overrides the standard price.
 */
export function effectiveUnitPrice(
  classDetails: PromoPricingClass,
  sessionStart?: string | Date | null
): number {
  if (classDetails.pricingMode === 'free') return 0;
  if (isPromoActiveForSession(classDetails, sessionStart)) {
    return Number(classDetails.promoPrice);
  }
  return Number(classDetails.price);
}
